/**
 * One §12.1 Provider over an index client (`indexClient.js` on desktop,
 * `webIndexClient.js` on web). The six provider files in
 * `src/palette/providers/` are this factory with their spec.
 *
 * - Scopes: records search `ctx.scopes` (personal, device, current teams);
 *   requests and collections live in API workspaces, so they search the
 *   current one (`ctx.currentWorkspaceId`) — the palette opens a request in
 *   the workspace that is open, as it always has.
 * - Documents stream (§12.1 rule 3): titles first, then the documents that
 *   match only by body text (desktop), appended, never more than `limit`.
 * - Requests and collections are offered only while the API Client is on
 *   (`apiClient: true` in their spec; #735's `isApiClientEnabled`).
 * - Documents carry the card 5.4 badge: the Documents sidebar's scope key and
 *   the personal workspaces' names are read once per search (`docContext`).
 * - `index_unavailable` hides the provider for the session; `no_db`,
 *   `db_locked`, `busy` and `query_too_long` answer nothing; an AbortError is
 *   rethrown (silent). The query is never logged.
 */
import { isAbortError } from './abort';
import { hitToResult, actionsFor } from './indexResults';
import { ORDER } from '../providers/order';
import { isApiClientEnabled } from '../../extensions/store';

const QUIET = new Set(['no_db', 'db_locked', 'busy', 'query_too_long', 'index_unavailable']);

/**
 * @param {{ id: string, kinds: string[], prefixes: string[], sigils?: string[], labelKey: string,
 *           icon: Function, order: number, page?: string, desktopOnly?: boolean,
 *           workspaceScoped?: boolean, body?: boolean, parentKind?: string, apiClient?: boolean }} spec
 * @param {{ client: object, getCollectionName?: (id: string) => string|undefined,
 *           isApiClientEnabled?: () => boolean,
 *           docContext?: () => Promise<{ currentKey?: string, scopeNames?: Map<string, string> }> }} deps
 */
export function createIndexProvider(spec, deps) {
  const client = deps.client;
  const apiClientOn = deps.isApiClientEnabled || isApiClientEnabled;
  const docContext = spec.body ? deps.docContext || defaultDocContext : null;
  let broken = false;
  const parentNames = new Map();

  const scopesOf = (ctx) => {
    if (spec.workspaceScoped) return ctx?.currentWorkspaceId ? [String(ctx.currentWorkspaceId)] : [];
    return ctx?.scopes || [];
  };

  function fail(err) {
    if (isAbortError(err)) throw err;
    if (err?.code === 'index_unavailable') broken = true;
    if (QUIET.has(err?.code)) return [];
    throw err;
  }

  /** Names of the parents (a task's board) the hits point at; one IPC for the unknown ones. */
  async function names(hits, ctx, signal) {
    if (spec.parentKind === 'collection' && deps.getCollectionName) {
      const m = new Map();
      for (const h of hits) if (h.parentId) m.set(String(h.parentId), deps.getCollectionName(String(h.parentId)));
      return m;
    }
    if (!spec.parentKind) return undefined;
    const missing = [...new Set(hits.map((h) => h.parentId).filter((p) => p && !parentNames.has(String(p))))];
    if (missing.length) {
      const got = await client
        .get({ refs: missing.slice(0, 200).map((id) => ({ kind: spec.parentKind, id: String(id) })), scopes: scopesOf(ctx) }, { signal })
        .catch((e) => (isAbortError(e) ? Promise.reject(e) : []));
      for (const h of got) parentNames.set(String(h.id), h.title);
      if (parentNames.size > 5000) parentNames.clear();
    }
    return parentNames;
  }

  async function query(req, body) {
    const res = await client.search(
      { q: req.text, kinds: spec.kinds, scopes: scopesOf(req.ctx), limit: Math.max(1, Math.min(200, req.limit || 6)), body },
      { signal: req.signal }
    );
    return res || { hits: [] };
  }

  async function toResults(hits, req) {
    const m = await names(hits, req.ctx, req.signal);
    const docs = docContext && hits.length ? await docContext().catch(() => undefined) : undefined;
    return hits.map((h) => hitToResult(spec.id, h, { names: m, docs }));
  }

  async function* streamDocuments(req) {
    const seen = new Set();
    let count = 0;
    try {
      const first = await query(req, false);
      const titles = await toResults(first.hits || [], req);
      for (const r of titles) seen.add(r.id);
      count = titles.length;
      if (req.signal?.aborted) return;
      yield titles;
      // The consumer may have aborted while it held the first batch.
      if (req.signal?.aborted || count >= req.limit || req.ctx?.platform === 'web') return;
      const second = await query(req, true);
      if (req.signal?.aborted) return;
      const extra = (second.hits || []).filter((h) => !seen.has(String(h.id))).slice(0, req.limit - count);
      if (extra.length) yield await toResults(extra, req);
    } catch (err) {
      const quiet = fail(err);
      if (quiet.length) yield quiet;
    }
  }

  return {
    id: spec.id,
    prefixes: spec.prefixes,
    ...(spec.sigils ? { sigils: spec.sigils } : {}),
    labelKey: spec.labelKey,
    icon: spec.icon,
    order: ORDER[spec.id] ?? spec.order,
    ...(spec.page ? { page: spec.page } : {}),
    kind: 'local-ipc',
    available: (ctx) =>
      !broken && !!ctx?.signedIn && !(spec.desktopOnly && ctx.platform !== 'desktop') && !(spec.apiClient && !apiClientOn()),
    search(req) {
      if (!String(req.text || '').trim()) return [];
      if (spec.body) return streamDocuments(req);
      return query(req, false)
        .then((res) => toResults(res.hits || [], req))
        .catch(fail);
    },
    async recent(ids, ctx) {
      const refs = ids.flatMap((id) => spec.kinds.map((kind) => ({ kind, id: String(id) }))).slice(0, 200);
      if (!refs.length) return [];
      try {
        const hits = await client.get({ refs, scopes: scopesOf(ctx) });
        const m = await names(hits, ctx);
        const docs = docContext && hits.length ? await docContext().catch(() => undefined) : undefined;
        return hits.map((h) => hitToResult(spec.id, h, { names: m, docs }));
      } catch (err) {
        return fail(err);
      }
    },
    actions: (result) => actionsFor(result),
    dispose() {
      parentNames.clear();
    },
  };
}

let docCache = { at: 0, value: null };

/**
 * The Documents sidebar's scope key and the personal workspaces' names, for
 * the card 5.4 badge. Imported lazily (the palette chunk stays free of the
 * documents extension) and cached for a few seconds.
 */
async function defaultDocContext() {
  if (docCache.value && Date.now() - docCache.at < 5000) return docCache.value;
  const [{ useDocumentsUiStore }, { listScopes }] = await Promise.all([
    import('../../extensions/bundled/documents/documentsUiStore'),
    import('../../extensions/bundled/documents/documentsScopes'),
  ]);
  const scopes = await listScopes().catch(() => []);
  const value = {
    currentKey: useDocumentsUiStore.getState().scopeKey || 'personal',
    scopeNames: new Map((scopes || []).filter((x) => x?.id != null).map((x) => [String(x.id), x.name || ''])),
  };
  docCache = { at: Date.now(), value };
  return value;
}
